import { useState, type CSSProperties } from 'react';
import { ApiError, apiSendJson } from '../api-client.js';
import { useAuth } from '../auth.js';

interface Props {
  org: string;
  overrideCount: number;
  hasDefault: boolean;
  onClose: () => void;
  onSaved: () => void;
}

const overlayStyle: CSSProperties = {
  position: 'fixed',
  inset: 0,
  background: 'rgba(0,0,0,0.45)',
  display: 'flex',
  alignItems: 'flex-start',
  justifyContent: 'center',
  padding: 'var(--loop-space-300)',
  zIndex: 200,
  overflowY: 'auto',
};

const dialogStyle: CSSProperties = {
  background: 'var(--loop-neutral-0)',
  borderRadius: 'var(--loop-radius-md)',
  padding: 'var(--loop-space-300)',
  width: '100%',
  maxWidth: 420,
  marginTop: 'var(--loop-space-300)',
  boxShadow: '0 12px 32px rgba(0,0,0,0.18)',
};

export function CascadeOptOutModal({ org, overrideCount, hasDefault, onClose, onSaved }: Props) {
  const { idToken } = useAuth();
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handleConfirm() {
    setSaving(true);
    setError(null);
    try {
      await apiSendJson(`/api/dv-config/org-optin/${encodeURIComponent(org)}`, 'DELETE', idToken!);
      onSaved();
    } catch (e) {
      if (e instanceof ApiError) {
        setError(e.body);
      } else {
        setError((e as Error).message);
      }
    } finally {
      setSaving(false);
    }
  }

  return (
    <div style={overlayStyle} onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div style={dialogStyle}>
        <h3 style={{ margin: '0 0 var(--loop-space-200)', fontSize: '1rem', fontWeight: 664, color: 'var(--loop-red-700)' }}>
          Opt out {org}
        </h3>

        <p style={{ fontSize: '0.875rem', color: 'var(--loop-neutral-900)', marginBottom: 'var(--loop-space-200)', lineHeight: 1.5 }}>
          Removing <strong>{org}</strong> will delete
          {hasDefault ? (
            <>
              {' '}its default config and <strong>{overrideCount}</strong> repo override{overrideCount !== 1 ? 's' : ''}
            </>
          ) : (
            <>
              {' '}<strong>{overrideCount}</strong> repo override{overrideCount !== 1 ? 's' : ''}
            </>
          )}.
          This is recorded in the audit log.
        </p>

        {error && (
          <div style={{ color: 'var(--loop-red-700)', fontSize: '0.8rem', marginBottom: 'var(--loop-space-150)', padding: 'var(--loop-space-100)', background: 'var(--loop-red-100)', borderRadius: 'var(--loop-radius-xs)' }}>
            {error}
          </div>
        )}

        <div style={{ display: 'flex', gap: 'var(--loop-space-100)', justifyContent: 'flex-end' }}>
          <button className="btn-secondary" onClick={onClose} disabled={saving}>
            Cancel
          </button>
          <button
            className="btn-primary"
            style={{ background: 'var(--loop-red-700)' }}
            onClick={() => { void handleConfirm(); }}
            disabled={saving}
          >
            {saving ? 'Removing…' : 'Confirm'}
          </button>
        </div>
      </div>
    </div>
  );
}
